import { env } from 'shared/config/env'
import {
  clearAuthTokens,
  getMpulseToken,
  getAccessToken,
  getRefreshToken,
  isAuthBootstrapInProgress,
  redirectToAuth,
  setAuthTokensFromBackend,
  setAuthUser,
  setAuthTokens,
  waitForAuthBootstrap,
} from 'shared/lib/auth'

export interface ApiError {
  status: number
  data: unknown
}

export interface HttpRequestOptions {
  signal?: AbortSignal
}

export interface HttpClient {
  get<T>(path: string, options?: HttpRequestOptions): Promise<T>
  post<T>(path: string, body: unknown, options?: HttpRequestOptions): Promise<T>
  delete<T>(path: string, options?: HttpRequestOptions): Promise<T>
}

let refreshPromise: Promise<string | null> | null = null
let webviewAuthPromise: Promise<boolean> | null = null
const DEFAULT_HTTP_TIMEOUT_MS = 15_000
const MUTATION_HTTP_TIMEOUT_MS = 30_000

function createTimeoutError(): ApiError {
  return {
    status: 0,
    data: { message: 'Превышено время ожидания ответа сервера' },
  }
}

function createNetworkRequestError(requestUrl: string, error: unknown): ApiError {
  const fallbackMessage = 'Не удалось выполнить запрос'

  if (!(error instanceof Error)) {
    return { status: 0, data: { message: fallbackMessage } }
  }

  const normalizedUrl = (() => {
    try {
      return new URL(requestUrl, window.location.origin)
    } catch {
      return null
    }
  })()

  if (
    normalizedUrl &&
    window.location.protocol === 'https:' &&
    normalizedUrl.protocol === 'http:'
  ) {
    return {
      status: 0,
      data: {
        message:
          'Запрос заблокирован браузером: страница открыта по HTTPS, а API использует HTTP (mixed content)',
      },
    }
  }

  // Browsers often surface CORS and other network failures as a generic TypeError.
  if (error instanceof TypeError) {
    return {
      status: 0,
      data: {
        message:
          'Сетевая ошибка или запрос заблокирован браузером (например, CORS). Проверьте доступность API и настройки CORS.',
      },
    }
  }

  const message = error.message.trim().length > 0 ? error.message : fallbackMessage
  return { status: 0, data: { message } }
}

export function isRequestAborted(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

function createRequestAbortedError() {
  return new DOMException('The request was cancelled', 'AbortError')
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs = DEFAULT_HTTP_TIMEOUT_MS,
  callerSignal?: AbortSignal
) {
  const controller = new AbortController()
  const abortForCaller = () => controller.abort(callerSignal?.reason)

  if (callerSignal?.aborted) {
    abortForCaller()
  } else {
    callerSignal?.addEventListener('abort', abortForCaller, { once: true })
  }

  const timeoutId = window.setTimeout(() => {
    controller.abort()
  }, timeoutMs)

  try {
    return await fetch(input, { ...init, signal: controller.signal })
  } catch (error) {
    if (callerSignal?.aborted) {
      throw createRequestAbortedError()
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw createTimeoutError()
    }

    throw error
  } finally {
    window.clearTimeout(timeoutId)
    callerSignal?.removeEventListener('abort', abortForCaller)
  }
}

function createFetchClient(): HttpClient {
  const baseUrl = env.apiBaseUrl.replace(/\/$/, '')
  const refreshUrl = `${baseUrl}/user/ad-refresh/`
  const webviewAuthUrl = `${baseUrl}/work/webview/auth/`

  const isAuthPath = (path: string) =>
    path.includes('/user/ad-login/') ||
    path.includes('/user/ad-refresh/') ||
    path.includes('/work/webview/auth/')

  const isCheckPath = (path: string) => /(^|\/)check(\/|$)/.test(path)

  const isProtectedWebviewPath = () => window.location.pathname.startsWith('/floor-6/map')

  const shouldTryWebviewAuthFallback = (path: string) =>
    isCheckPath(path) && isProtectedWebviewPath()

  async function refreshAccessToken(): Promise<string | null> {
    const refresh = getRefreshToken()
    if (!refresh) return null

    if (refreshPromise) return refreshPromise

    refreshPromise = (async () => {
      const response = await fetchWithTimeout(refreshUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      })

      if (!response.ok) {
        throw new Error('refresh-failed')
      }

      const data = (await response.json()) as { access: string; refresh: string }
      if (!data.access || !data.refresh) {
        throw new Error('invalid-refresh-response')
      }
      setAuthTokens(data)
      return data.access
    })()
      .catch(() => {
        clearAuthTokens()
        redirectToAuth()
        return null
      })
      .finally(() => {
        refreshPromise = null
      })

    return refreshPromise
  }

  async function authenticateWithWebviewToken(): Promise<boolean> {
    const mpulseToken = getMpulseToken()?.trim()
    if (!mpulseToken) return false

    if (webviewAuthPromise) return webviewAuthPromise

    webviewAuthPromise = (async () => {
      const response = await fetchWithTimeout(webviewAuthUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: mpulseToken }),
      })

      if (!response.ok) {
        return false
      }

      const data = (await response.json()) as {
        auth?: { access_token?: string; refresh_token?: string }
        user?: unknown
      }

      if (!data.auth?.access_token || !data.auth?.refresh_token || !data.user) {
        return false
      }

      setAuthTokensFromBackend({
        access_token: data.auth.access_token,
        refresh_token: data.auth.refresh_token,
      })
      setAuthUser(data.user as Parameters<typeof setAuthUser>[0])
      return true
    })()
      .catch(() => false)
      .finally(() => {
        webviewAuthPromise = null
      })

    return webviewAuthPromise
  }

  async function request<T>(
    method: 'GET' | 'POST' | 'DELETE',
    path: string,
    body?: unknown,
    isRetry = false,
    options?: HttpRequestOptions
  ): Promise<T> {
    if (!isAuthPath(path) && isAuthBootstrapInProgress()) {
      await waitForAuthBootstrap()
    }

    const url = path.startsWith('http') ? path : `${baseUrl}/${path.replace(/^\//, '')}`
    const accessToken = getAccessToken()
    const headers: Record<string, string> = {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    }
    const init: RequestInit = {
      method,
      headers,
    }
    const timeoutMs =
      method === 'POST' || method === 'DELETE' ? MUTATION_HTTP_TIMEOUT_MS : DEFAULT_HTTP_TIMEOUT_MS
    if (body && method === 'POST') {
      headers['Content-Type'] = 'application/json'
      init.body = JSON.stringify(body)
    }

    let response: Response
    try {
      response = await fetchWithTimeout(url, init, timeoutMs, options?.signal)
    } catch (error) {
      if (isRequestAborted(error)) {
        throw error
      }
      if (error && typeof error === 'object' && 'status' in error && 'data' in error) {
        throw error
      }
      throw createNetworkRequestError(url, error)
    }

    if (response.status === 401 && !isRetry && !isAuthPath(path)) {
      const nextAccess = await refreshAccessToken()
      if (nextAccess) {
        return request<T>(method, path, body, true, options)
      }

      if (shouldTryWebviewAuthFallback(path)) {
        const didAuthenticate = await authenticateWithWebviewToken()
        if (didAuthenticate) {
          return request<T>(method, path, body, true, options)
        }
      }
    }

    if (!response.ok) {
      if (response.status === 401 && !isAuthPath(path)) {
        clearAuthTokens()
        redirectToAuth()
      }
      let data: unknown = {}
      const responseText = await response.text()
      if (responseText.trim().length > 0) {
        try {
          data = JSON.parse(responseText) as unknown
        } catch {
          data = { message: responseText }
        }
      } else {
        data = { message: response.statusText }
      }
      const err: ApiError = { status: response.status, data }
      throw err
    }

    const text = await response.text()
    if (!text) return undefined as T
    return JSON.parse(text) as T
  }

  return {
    get: <T>(path: string, options?: HttpRequestOptions) =>
      request<T>('GET', path, undefined, false, options),
    post: <T>(path: string, body: unknown, options?: HttpRequestOptions) =>
      request<T>('POST', path, body, false, options),
    delete: <T>(path: string, options?: HttpRequestOptions) =>
      request<T>('DELETE', path, undefined, false, options),
  }
}

let clientInstance: HttpClient | null = null

export function createHttpClient(): HttpClient {
  if (!clientInstance) {
    clientInstance = createFetchClient()
  }
  return clientInstance
}
